<template>
	<div
		class="form__field"
		:class="{
			'form__field--has-error': error,
			'form__field--menu-style': menuStyle
		}"
	>
		<div class="form__field__input-wrapper">
			<input
				:value="phone"
				v-mask="phoneMask"
				ref="inputRef"
				class="form__field__input"
				type="text"
				inputmode="numeric"
				required
				:placeholder="phone ? '' : 'Phone Number'"
				:disabled="disabled"
				:id="inputId"
				:key="key"
				autocomplete="off"
				@focus="handleFocus"
				@input.prevent="handleInput"
				@keyup.enter="onEnter"
				@change.prevent="onChange"
				@blur="onBlur"
			/>
			<label :for="inputId" class="form__field__label">
				{{ label }}
			</label>
			<div
				v-if="phone && cleanable"
				class="form__field__clean-icon"
				tabindex="-1"
				@click.stop="cleanValue"
			>
				<img :src="closeIcon" alt="" />
			</div>
			<div v-if="error" class="form__field__error">
				<img
					v-if="errorIconVisible"
					class="form__field__error__icon"
					:src="errorIcon"
					alt="error icon"
				/>
				<span>{{ error }}</span>
			</div>
			<div
				v-else-if="infoMessage"
				class="form__field__info"
				:class="{ 'form__field__info--success': isInfoMessageSuccess }"
			>
				<img
					class="form__field__info__icon"
					:src="checkIcon"
					alt="check icon"
				/>
				<span>{{ infoMessage }}</span>
			</div>
		</div>
	</div>
</template>

<script setup>
// TODO: Rename component
// Vue
import {
	computed,
	nextTick,
	onBeforeMount,
	onBeforeUnmount,
	ref,
	watch
} from 'vue'
import { useInput } from '@/components/Ticketing/Controls/script/useInput'
// Utils
import { v4 as uuidv4 } from 'uuid'
import { VueMaskFilter } from 'v-mask'
// Icons
import ErrorIcon from '@/assets/images/ic_info_error.svg'
import CheckIcon from '@/assets/images/ic_check_small.svg'
import CloseIcon from '@/assets/images/ic_cancel_grey.svg'
import { createVuexHelpers } from 'vue2-helpers'

// Props
const props = defineProps({
	value: {
		type: String,
		default: ''
	},

	autoSelectText: {
		type: Boolean,
		default: false
	},

	error: {
		type: String,
		default: ''
	},

	label: {
		type: String,
		default: 'Phone Number'
	},

	debounced: {
		type: Boolean,
		default: false
	},

	disabled: {
		type: Boolean,
		default: false
	},

	infoMessage: {
		type: String,
		default: ''
	},

	cleanable: {
		type: Boolean,
		default: false
	},

	menuStyle: {
		type: Boolean,
		default: false
	},

	errorIconVisible: {
		type: Boolean,
		default: true
	}
})

const { label } = props

// Store
const { useState } = createVuexHelpers()
const { isMacOrIOS } = useState('ui', ['isMacOrIOS'])

// Emits
const emit = defineEmits(['input', 'enter', 'change', 'blur', 'clean'])

// Refs
const phone = ref('')
const phoneMask = ref('(###) ###-####')
const inputId = ref(uuidv4())
const key = ref(uuidv4())
const inputRef = ref(null)
const timeout = ref(0)

// Computed
const errorIcon = computed(() => ErrorIcon)
const checkIcon = computed(() => CheckIcon)
const closeIcon = computed(() => CloseIcon)

watch(props, newVal => {
	setInitialMask(newVal?.value)
})

// Hooks
onBeforeMount(() => {
	setInitialMask(props?.value)
})

onBeforeUnmount(() => {
	if (timeout.value) {
		clearTimeout(timeout.value)
	}
})

// Composable
const {
	handleFocus,
	onEnter,
	emitInput,
	onChange,
	isInfoMessageSuccess
} = useInput(props, phone, emit)

// Methods
const setInitialMask = phone => {
	if (!phone) {
		phoneMask.value = '(###) ###-####'
	}

	if (
		phone?.length === 17 &&
		phone?.includes('+1 ') &&
		phoneMask.value !== '+1 (###) ###-####'
	) {
		phoneMask.value = '+1 (###) ###-####'
	}

	if (
		(phone.length === 17 || (phone.includes('+380') && phone.length === 13)) &&
		phoneMask.value !== '+### ## ###-##-##'
	) {
		phoneMask.value = '+### ## ###-##-##'
	}

	if (
		(phone.length === 15 || (phone.includes('+48') && phone.length === 12)) &&
		phoneMask.value !== '+## ### ### ###'
	) {
		phoneMask.value = '+## ### ### ###'
	}
}

const handleInput = e => {
	const value = e.target.value

	// Autofill USA case - 11 phone number chars without +1
	if (
		value.length === 11 &&
		value[0] === '1' &&
		!value.includes('(') &&
		!value.includes('-')
	) {
		const maskedValue = VueMaskFilter(value.slice(1), '(###) ###-####')
		emit('blur', maskedValue)
		e.target.value = maskedValue

		e.preventDefault()
		timeout.value = setTimeout(() => (key.value = uuidv4()), 100)

		return
	}
	// UA
	if (value.includes('(380)') && value.length > 14) {
		phoneMask.value = '+### ## ###-##-##'
	}
	// PL
	if (value.includes('(48') && value.length > 14) {
		phoneMask.value = '+## ### ### ###'
	}
	// USA
	if (
		phoneMask.value !== '(###) ###-####' &&
		value.length <= 14 &&
		!(value[0] === '1' || value[1] === '1')
	) {
		phoneMask.value = '(###) ###-####'
	}
	// USA with code
	if (
		phoneMask.value !== '+1 (###) ###-####' &&
		value !== '(1' &&
		(value[0] === '1' || (value[1] === '1' && value[0] === '+'))
	) {
		phoneMask.value = '+1 (###) ###-####'

		if (value.length === 1) {
			phone.value = '+1'

			return
		}
	}

	if (
		phoneMask.value === '+1 (###) ###-####' &&
		value.length === 1 &&
		phone.value.length > 1
	) {
		phone.value = '('
		phoneMask.value = '(###) ###-####'
	}

	// Autofill USA case - 10 phone number chars without +1
	if (value.length === 10 && !value.includes('(') && !value.includes('-')) {
		timeout.value = setTimeout(() => {
			key.value = uuidv4()
			nextTick(() => {
				emit('blur', phone.value)
			})
		}, 100)
	}

	phone.value = value
	emitInput()
}

const onBlur = () => {
	emit('blur', phone.value)
}

const focusInput = () => {
	if (inputRef.value) inputRef.value.focus()
}

const cleanValue = () => {
	phone.value = ''
	emit('clean')
	// inputRef.value.dispatchEvent(new Event('input', { bubbles: true }))
	if (isMacOrIOS.value) {
		key.value = uuidv4()
	} else {
		focusInput()
	}
}

// Expose
defineExpose({
	focusInput
})
</script>

<style lang="scss" scoped>
@import 'styles/_input.scss';
</style>
